import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";
import { Member } from "@/types";
import { getMemberDisplayName } from "../constants";
import { CountBadge } from "@/components/shared/CountBadge";

interface Country {
  id: number;
  amazon_country: string;
}

interface AddBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newBrandName: string;
  onNewBrandNameChange: (name: string) => void;
  newBrandCountryId: string;
  onNewBrandCountryIdChange: (id: string) => void;
  countries: Country[];
  activeMembers: Member[];
  assignedUserIds: number[];
  onToggleAssignedUser: (userId: number) => void;
  onSelectAllMembers: () => void;
  allMembersSelected: boolean;
  onAdd: () => void;
}

export function AddBrandDialog({
  open,
  onOpenChange,
  newBrandName,
  onNewBrandNameChange,
  newBrandCountryId,
  onNewBrandCountryIdChange,
  countries,
  activeMembers,
  assignedUserIds,
  onToggleAssignedUser,
  onSelectAllMembers,
  allMembersSelected,
  onAdd,
}: AddBrandDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] bg-card">
        <DialogHeader>
          <DialogTitle>Add New Brand</DialogTitle>
          <DialogDescription>Add a new brand to organize your products and keywords.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Brand Name</Label>
            <Input
              placeholder="Enter brand name"
              value={newBrandName}
              onChange={(e) => onNewBrandNameChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onAdd()}
              className="h-9 rounded-lg bg-card"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Marketplace</Label>
            <Select value={newBrandCountryId} onValueChange={onNewBrandCountryIdChange}>
              <SelectTrigger className="h-9 rounded-lg bg-card">
                <SelectValue placeholder="Select marketplace" />
              </SelectTrigger>
              <SelectContent className="bg-popover border border-border">
                {countries.filter((country) => country.amazon_country === "US").map((country) => (
                  <SelectItem key={country.id} value={String(country.id)}>
                    Amazon {country.amazon_country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs inline-flex items-center gap-1.5">
                Assigned Members <CountBadge size="sm" count={assignedUserIds.length} /> selected
              </Label>
              {activeMembers.length > 0 && (
                <button
                  type="button"
                  onClick={onSelectAllMembers}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  {allMembersSelected ? "Deselect All" : "Select All"}
                </button>
              )}
            </div>
            <div className="border border-border rounded-lg max-h-[200px] overflow-y-auto divide-y divide-border">
              {activeMembers.map((member) => {
                const isSelected = assignedUserIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => onToggleAssignedUser(member.id)}
                    className="flex items-center gap-2.5 w-full px-3 py-2 hover:bg-muted/30 text-sm text-foreground transition-colors text-left"
                  >
                    <div className={`h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${isSelected ? "bg-primary border-primary" : "border-border"}`}>
                      {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <span>{getMemberDisplayName(member)}</span>
                    <span className="text-xs text-muted-foreground ml-auto capitalize">{member.role}</span>
                  </button>
                );
              })}
              {activeMembers.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No active members</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">Cancel</Button>
          <Button onClick={onAdd} disabled={!newBrandName.trim()} className="rounded-lg">Add Brand</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
